import { useState, useEffect, useRef } from 'react';

import Validate from './validate';

type GetTimeFromSeconds = { seconds: number; minutes: number; hours: number; days: number };

class Time {
  static getTimeFromSeconds(secs: number): GetTimeFromSeconds {
    const totalSeconds = Math.ceil(secs);
    const days = Math.floor(totalSeconds / (60 * 60 * 24));
    const hours = Math.floor((totalSeconds % (60 * 60 * 24)) / (60 * 60));
    const minutes = Math.floor((totalSeconds % (60 * 60)) / 60);
    const seconds = Math.floor(totalSeconds % 60);

    return {
      seconds,
      minutes,
      hours,
      days,
    };
  }

  static getSecondsFromExpiry(expiry: number) {
    const now = new Date().getTime();
    const milliSecondsDistance = expiry - now;
    if (milliSecondsDistance > 0) {
      return milliSecondsDistance / 1000;
    }
    return 0;
  }

  static getSecondsFromTimeNow() {
    const now = new Date();
    const currentTimestamp = now.getTime();
    const offset = now.getTimezoneOffset() * 60;
    return currentTimestamp / 1000 - offset;
  }

  static getFormattedTimeFromSeconds(
    totalSeconds: number,
    format: '12-hour' | '24-hours' = '24-hours',
  ) {
    const { seconds: secondsValue, minutes, hours } = Time.getTimeFromSeconds(totalSeconds);
    let ampm = '';
    let hoursValue = hours;

    if (format === '12-hour') {
      ampm = hours >= 12 ? 'pm' : 'am';
      hoursValue = hours % 12;
    }

    return {
      seconds: secondsValue,
      minutes,
      hours: hoursValue,
      ampm,
    };
  }
}

type UseTimerProps = {
  expiryTimestamp: number;
  onExpire?: () => void;
  expiry?: number;
};

type UseTimerType = GetTimeFromSeconds & {
  start: () => void;
  pause: () => void;
  resume: () => void;
  restart: (newExpiryTimestamp: number) => void;
  isRunning: boolean;
};

export default function useTimer(settings: UseTimerProps): UseTimerType {
  const { expiryTimestamp: expiry, onExpire = () => {} } = settings;
  const [expiryTimestamp, setExpiryTimestamp] = useState(expiry);
  const [seconds, setSeconds] = useState(Time.getSecondsFromExpiry(expiryTimestamp));
  const [isRunning, setIsRunning] = useState(true);
  const intervalRef = useRef<number>();

  function clearIntervalRef() {
    if (intervalRef.current) {
      setIsRunning(false);
      clearInterval(intervalRef.current);
      intervalRef.current = undefined;
    }
  }

  function handleExpire() {
    clearIntervalRef();
    if (Validate.onExpire(onExpire)) {
      onExpire();
    }
  }

  function start() {
    if (!intervalRef.current) {
      setIsRunning(true);
      intervalRef.current = window.setInterval(() => {
        const secondsValue = Time.getSecondsFromExpiry(expiryTimestamp);
        if (secondsValue <= 0) {
          handleExpire();
        }
        setSeconds(secondsValue);
      }, 1000);
    }
  }

  function pause() {
    clearIntervalRef();
  }

  function resume() {
    if (!intervalRef.current) {
      setIsRunning(true);
      if (window !== undefined) {
        intervalRef.current = window.setInterval(
          () =>
            setSeconds((prevSeconds) => {
              const secondsValue = prevSeconds - 1;
              if (secondsValue <= 0) {
                handleExpire();
              }
              return secondsValue;
            }),
          1000,
        );
      }
    }
  }

  function restart(newExpiryTimestamp: number) {
    clearIntervalRef();
    setExpiryTimestamp(newExpiryTimestamp);
  }

  function handleExtraMilliSeconds(secondsValue: number, extraMilliSeconds: number) {
    setIsRunning(true);
    intervalRef.current = window.setTimeout(() => {
      const currentSeconds = Time.getSecondsFromExpiry(expiryTimestamp);
      setSeconds(currentSeconds);
      if (currentSeconds <= 0) {
        handleExpire();
      } else {
        intervalRef.current = undefined;
        start();
      }
    }, extraMilliSeconds);
  }

  useEffect(() => {
    if (Validate.expiryTimestamp(expiryTimestamp)) {
      const secondsValue = Time.getSecondsFromExpiry(expiryTimestamp);
      const extraMilliSeconds = Math.floor((secondsValue - Math.floor(secondsValue)) * 1000);
      setSeconds(secondsValue);
      if (extraMilliSeconds > 0) {
        handleExtraMilliSeconds(secondsValue, extraMilliSeconds);
      } else {
        start();
      }
    }
    return clearIntervalRef;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiryTimestamp]);

  return {
    ...Time.getTimeFromSeconds(seconds),
    start,
    pause,
    resume,
    restart,
    isRunning,
  };
}
